import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Activity, AlertCircle, CheckCircle2 } from 'lucide-react';
import { jobFailing, jobStatusLines } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { useSystemHealth } from '../../services/health';
import { outlineButton } from '../../components/styles';

const ago = (iso: string) => formatDistanceToNow(new Date(iso), { addSuffix: true });

/**
 * The owner's status of everything that runs on a schedule (CER-69): each
 * job's last run and result, the last completed run of the function it wakes
 * and that function's last answer, and every problem there is now. Read from
 * the database when shown, so it is true even when push or the watch is what
 * broke. Nothing for an account that is not an owner.
 */
export default function SystemStatus() {
  const query = useSystemHealth();
  // Arrived from Today's warning (/settings#system-status): shown, scrolled to and opened.
  const linked = useLocation().hash === '#system-status';
  const section = useRef<HTMLElement>(null);
  const ready = query.isSuccess && query.data !== null;
  useEffect(() => {
    if (linked && ready) section.current?.scrollIntoView?.({ block: 'start' });
  }, [linked, ready]);
  if (query.isPending || query.data === null) return null;

  return (
    <section ref={section} className="bg-surface rounded-xl p-4 sm:p-6 shadow-sm scroll-mt-4" aria-labelledby="system-status">
      <h2 id="system-status" className="flex items-center gap-2 text-lg font-semibold mb-4">
        <Activity className="w-5 h-5" />
        System status
      </h2>
      {query.isError ? (
        <p className="text-sm text-error">The system status could not be loaded: {errorMessage(query.error)}</p>
      ) : (
        // Closed: one line. Open, on purpose: the problems, every job, and when it was checked (CER-231).
        <details className="space-y-4" open={linked || undefined}>
          <summary className={`cursor-pointer text-sm ${query.data.problems.length === 0 ? 'text-on-surface-variant' : 'text-error font-medium'}`}>
            {query.data.problems.length === 0
              ? 'Nothing is wrong right now.'
              : `${query.data.problems.length} ${query.data.problems.length === 1 ? 'problem' : 'problems'} right now.`}
          </summary>
          {query.data.problems.length > 0 && (
            <ul className="space-y-2">
              {query.data.problems.map((problem) => (
                <li key={problem.key} className="text-sm">
                  <p className="text-error">{problem.summary}</p>
                  <p className="text-on-surface-variant">{problem.since ? `Since ${ago(problem.since)}` : 'Just found'}</p>
                </li>
              ))}
            </ul>
          )}
          <ul className="space-y-3">
            {query.data.jobs.map((job) => {
              const failing = jobFailing(job);
              const Icon = failing ? AlertCircle : CheckCircle2;
              return (
                <li key={job.name} className="flex gap-3">
                  <Icon className={`w-5 h-5 shrink-0 ${failing ? 'text-error' : 'text-success'}`} aria-label={failing ? 'Failing' : 'Working'} />
                  <div className="text-sm">
                    <p className="font-medium">{job.label}</p>
                    {jobStatusLines(job, ago).map((line) => (
                      <p key={line} className="text-on-surface-variant">
                        {line}
                      </p>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs text-on-surface-variant">Checked {ago(query.data.checked_at)}</p>
            <button type="button" onClick={() => query.refetch()} disabled={query.isFetching} className={`${outlineButton} text-xs`}>
              {query.isFetching ? 'Checking…' : 'Check again'}
            </button>
          </div>
        </details>
      )}
    </section>
  );
}
